import type { Elplan } from 'api/elplan/elplan.api'

export type ElplanSlotUnit = 'hour' | 'quarter'

const SLOT_UNITS: readonly string[] = ['hour', 'quarter'] satisfies ElplanSlotUnit[]

export function isElplanSlotUnit(unit: string): unit is ElplanSlotUnit {
  return SLOT_UNITS.includes(unit)
}

export type ElplanDraft = {
  resolution: ElplanSlotUnit
  slotStarts: readonly ISODateTime[]
  textByStart: Readonly<Record<ISODateTime, string>>
}

export type ElplanSlotEdit = {
  start: ISODateTime
  text: string
}

export type ElplanSlot = {
  unit: ElplanSlotUnit
  start: ISODateTime
}

export type ElplanSummaryTextEntry = 'character' | 'paste'

export type ElplanCellKey = `${ElplanSlotUnit}@${ISODateTime}`

export type ElplanEditorRow =
  | { kind: 'hour'; start: ISODateTime; label: string; isExpanded: boolean }
  | { kind: 'quarter'; start: ISODateTime; label: string }

type ElplanVolumeFault = 'not-a-number' | 'too-many-decimals'

export type ElplanVolumeReading =
  | { kind: 'blank' }
  | { kind: 'value'; volumeMw: number }
  | { kind: 'fault'; fault: ElplanVolumeFault }

export type ElplanShownReading = ElplanVolumeReading | { kind: 'unfinished' }

export type ElplanHourReading =
  | { kind: 'blank' }
  | { kind: 'partial'; shownMeanMw: number }
  | { kind: 'value'; volumeMw: number; quarterVolumesMw: readonly number[] }
  | Extract<ElplanVolumeReading, { kind: 'fault' }>
  | { kind: 'unfinished' }

export type ElplanRowRead = {
  isSummary: boolean
  valueText: string
  displayText: string
  hasEmptyValues: boolean
  fault: ElplanVolumeFault | undefined
  isChanged: boolean
}

export type ElplanHourFlatten =
  | { kind: 'refused'; partlyBlankHourStarts: ISODateTime[]; faultedHourStarts: ISODateTime[] }
  | { kind: 'flattens'; edits: ElplanSlotEdit[]; changedHourCount: number }

export type ElplanModeSwitchOutcome =
  | { kind: 'switched' }
  | { kind: 'unchanged' }
  | { kind: 'refused'; partlyBlankHourCount: number; faultedHourCount: number }
  | { kind: 'needsConfirmation'; changedHourCount: number }

export type ElplanSlotFaults = {
  readingByStart: Readonly<Record<ISODateTime, ElplanVolumeReading>>
  shownReadingByStart: Readonly<Record<ISODateTime, ElplanShownReading>>
  blocksSave: boolean
}

export type ElplanSaveTarget =
  | { kind: 'create'; elplanInfoId: number; day: ISODateTime }
  | { kind: 'update'; elplanId: number; valueIdByStart: Readonly<Record<ISODateTime, number>> }

export type ElplanEditorSession = {
  seed: ElplanDraft
  target: ElplanSaveTarget
}

export type ElplanSessionSource =
  | { kind: 'empty'; elplanInfoId: number }
  | { kind: 'fromBids'; elplanInfoId: number; bidSumMwDataset: Pick<Dataset, 'times' | 'values'> }
  | { kind: 'edit'; elplan: Pick<Elplan, 'id' | 'elplan_values'> }
